// ============================================================
// Types TypeScript — Module Admin Hirafi
// ============================================================

export type AdminRole = 'adminOwner' | 'adminModerator' | 'adminTech';
export type UserStatus = 'active' | 'suspended' | 'banned' | 'deleted';
export type VerificationStatus = 'notRequested' | 'pending' | 'verified' | 'rejected';
export type ModerationStatus = 'active' | 'underReview' | 'hidden' | 'removed';
export type ReportStatus = 'open' | 'inProgress' | 'resolved' | 'dismissed';
export type ReportType = 'user' | 'ad' | 'message' | 'photo';

export type AuditActionType =
  | 'user_suspended'
  | 'user_reactivated'
  | 'user_deleted'
  | 'artisan_verified'
  | 'artisan_rejected'
  | 'ad_hidden'
  | 'ad_removed'
  | 'ad_marked_safe'
  | 'report_resolved'
  | 'report_dismissed'
  | 'report_in_progress'
  | 'banner_created'
  | 'banner_updated'
  | 'banner_archived';

// -------------------------
// Entités admin
// -------------------------

export interface AdminUser {
  id: string;
  userId: string;
  adminRole: AdminRole;
  createdAt: string;
  createdByAdminId: string | null;
}

export interface Report {
  id: string;
  reportType: ReportType;
  reporterUserId: string | null;
  targetId: string;
  targetType: ReportType;
  reason: string;
  details: string | null;
  status: ReportStatus;
  resolvedByAdminId: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuditLog {
  id: string;
  timestamp: string;
  adminUserId: string;
  actionType: AuditActionType;
  targetType: string;
  targetId: string;
  reasonCode: string | null;
  notes: string | null;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
}

// -------------------------
// Vues join (pour les tables)
// -------------------------

export interface VerificationQueueItem {
  artisanProfileId: string;
  userId: string;
  fullName: string;
  email: string;
  specialty: string;
  wilaya: string;
  verificationStatus: VerificationStatus;
  verificationRequestedAt: string | null;
  verifiedAt: string | null;
  rejectionReasonCode: string | null;
  verificationNotes: string | null;
  avatarUrl: string | null;
}

export interface AdminAnnonceRow {
  id: string;
  title: string;
  userId: string;
  authorName: string;
  wilaya: string;
  commune: string;
  metier: string;
  status: string;
  moderationStatus: ModerationStatus;
  removedAt: string | null;
  removeReasonCode: string | null;
  moderationNotes: string | null;
  createdAt: string;
}

export interface AdminUserRow {
  id: string;
  userId: string;
  fullName: string;
  email: string;
  role: 'client' | 'artisan' | 'artisan_entreprise' | 'artisan_bricoleur' | 'admin';
  status: UserStatus;
  suspendedAt: string | null;
  suspensionReason: string | null;
  deletedAt: string | null;
  createdAt: string;
  avatarUrl: string | null;
  wilaya: string | null;
  specialite: string | null;
  verificationStatus: VerificationStatus | null;
}

export interface ReportAdTarget {
  title: string;
  description: string | null;
  wilaya: string;
  commune: string;
  metier: string;
  createdAt: string;
  authorName: string;
  authorUserId: string;
}

export interface ReportUserTarget {
  displayName: string;
  userId: string;
  wilaya: string | null;
  specialite: string | null;
}

export interface AdminReportRow extends Report {
  reporterName: string | null;
  adTarget: ReportAdTarget | null;
  userTarget: ReportUserTarget | null;
}

export interface AdminAuditLogRow extends AuditLog {
  adminFullName: string;
}

// -------------------------
// Constantes : codes motif
// -------------------------

export const REASON_CODES: Record<string, string[]> = {
  verification_rejection: [
    'documents_invalides',
    'documents_manquants',
    'identite_non_confirmee',
    'activite_non_confirmee',
    'fraude_suspectee',
    'autre',
  ],
  user_suspension: [
    'comportement_abusif',
    'spam',
    'fraude',
    'fausse_identite',
    'contenu_inapproprie',
    'signalements_multiples',
    'autre',
  ],
  user_deletion: [
    'demande_utilisateur',
    'fraude_confirmee',
    'compte_inactif',
    'violation_cgu',
    'autre',
  ],
  ad_moderation: [
    'contenu_inapproprie',
    'spam',
    'prix_abusif',
    'doublon',
    'hors_perimetre',
    'fraude',
    'autre',
  ],
  report_resolution: [
    'action_prise',
    'sans_suite_infondé',
    'hors_perimetre',
    'deja_traite',
    'autre',
  ],
};

export const REASON_CODE_LABELS: Record<string, string> = {
  documents_invalides: 'Documents invalides',
  documents_manquants: 'Documents manquants',
  identite_non_confirmee: 'Identité non confirmée',
  activite_non_confirmee: 'Activité non confirmée',
  fraude_suspectee: 'Fraude suspectée',
  comportement_abusif: 'Comportement abusif',
  spam: 'Spam',
  fraude: 'Fraude',
  fausse_identite: 'Fausse identité',
  contenu_inapproprie: 'Contenu inapproprié',
  signalements_multiples: 'Signalements multiples',
  demande_utilisateur: 'Demande de l\'utilisateur',
  fraude_confirmee: 'Fraude confirmée',
  compte_inactif: 'Compte inactif',
  violation_cgu: 'Violation des CGU',
  prix_abusif: 'Prix abusif',
  doublon: 'Annonce en doublon',
  hors_perimetre: 'Hors périmètre MVP',
  action_prise: 'Action prise',
  sans_suite_infondé: 'Classé sans suite (infondé)',
  deja_traite: 'Déjà traité',
  autre: 'Autre',
};

export const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  adminOwner: 'Owner',
  adminModerator: 'Modérateur',
  adminTech: 'Tech',
};

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  active: 'Actif',
  suspended: 'Suspendu',
  banned: 'Banni',
  deleted: 'Supprimé',
};

export const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, string> = {
  notRequested: 'Non demandé',
  pending: 'En attente',
  verified: 'Vérifié',
  rejected: 'Rejeté',
};

export const MODERATION_STATUS_LABELS: Record<ModerationStatus, string> = {
  active: 'Active',
  underReview: 'En révision',
  hidden: 'Masquée',
  removed: 'Supprimée',
};

export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  open: 'Ouvert',
  inProgress: 'En cours',
  resolved: 'Résolu',
  dismissed: 'Classé',
};
